// packages/api/src/PublicSupporterService.ts
//
// ROADMAP #284 — the supporter page: one person's gifts, memberships and events across a brand's organizers, opened by an
// emailed code. BROWSER only, through the same-origin proxy (which names the brand).
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";

export interface PublicSupporterGift {
  causeName?: string | null;
  /** The event it was given at; none for a gift on the organizer's page. */
  eventTitle?: string | null;
  amount?: number | null;
  currencyCode?: string | null;
  receivedAt?: string | null;
  donationNumber?: string | null;
  /** received | refunded */
  donationStatus?: string | null;
  isMonthly?: boolean | null;
  isTest?: boolean | null;
  receiptId?: string | null;
  receiptNumber?: string | null;
}

export interface PublicSupporterYearReceipt {
  id?: string | null;
  receiptNumber?: string | null;
  taxYear?: number | null;
  amount?: number | null;
  currencyCode?: string | null;
}

export interface PublicSupporterMember {
  id?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  isPrimary?: boolean | null;
  isYou?: boolean | null;
}

export interface PublicSupporterMembership {
  id?: string | null;
  typeName?: string | null;
  membershipNumber?: string | null;
  startsOn?: string | null;
  expiresOn?: string | null;
  isExpired?: boolean | null;
  isTest?: boolean | null;
  /** How many uses the number holds (null = no limit). */
  usesAllowed?: number | null;
  usesLeft?: number | null;
  usesCount?: number | null;
  /** People it covers (null = no limit). */
  maxMembers?: number | null;
  canEditHousehold?: boolean | null;
  members?: PublicSupporterMember[] | null;
}

export interface PublicSupporterMonthly {
  name?: string | null;
  amount?: number | null;
  currencyCode?: string | null;
  billingInterval?: string | null;
  recurringStatus?: string | null;
  nextChargeAt?: string | null;
  isTest?: boolean | null;
  manageUrl?: string | null;
}

export interface PublicSupporterEvent {
  title?: string | null;
  url?: string | null;
  startsAt?: string | null;
  timezone?: string | null;
  rsvpStatus?: string | null;
  checkedIn?: boolean | null;
}

export interface PublicSupporterOrganizer {
  name?: string | null;
  url?: string | null;
  timezone?: string | null;
  gifts?: PublicSupporterGift[] | null;
  receipts?: PublicSupporterYearReceipt[] | null;
  memberships?: PublicSupporterMembership[] | null;
  monthly?: PublicSupporterMonthly[] | null;
  events?: PublicSupporterEvent[] | null;
}

export interface PublicSupporterPage {
  email?: string | null;
  /** The newest rows only: this person has more than one page reads. */
  cut?: boolean | null;
  /** Where to make an account of this brand. */
  accountUrl?: string | null;
  organizers?: PublicSupporterOrganizer[] | null;
}

const PAGE_QUERY = gql`
  query PublicSupporterPage($params: PublicSupporterTokenInput) {
    publicSupporterPage(params: $params) {
      code
      errors { name errors }
      data {
        email
        cut
        accountUrl
        organizers {
          name
          url
          timezone
          gifts { causeName eventTitle amount currencyCode receivedAt donationNumber donationStatus isMonthly isTest receiptId receiptNumber }
          receipts { id receiptNumber taxYear amount currencyCode }
          memberships {
            id typeName membershipNumber startsOn expiresOn isExpired isTest usesAllowed usesLeft usesCount maxMembers canEditHousehold
            members { id firstName lastName isPrimary isYou }
          }
          monthly { name amount currencyCode billingInterval recurringStatus nextChargeAt isTest manageUrl }
          events { title url startsAt timezone rsvpStatus checkedIn }
        }
      }
    }
  }
`;

const RECEIPT_QUERY = gql`
  query PublicSupporterReceiptPdf($params: PublicSupporterReceiptInput) {
    publicSupporterReceiptPdf(params: $params) {
      code
      errors { name errors }
      data { fileUrl filename }
    }
  }
`;

const REQUEST_CODE = gql`
  mutation PublicSupporterRequestCode($params: PublicSupporterRequestCodeInput) {
    publicSupporterRequestCode(params: $params) {
      code
      errors { name errors }
      data { sent }
    }
  }
`;

const REDEEM_CODE = gql`
  mutation PublicSupporterRedeemCode($params: PublicSupporterRedeemCodeInput) {
    publicSupporterRedeemCode(params: $params) {
      code
      errors { name errors }
      data { token email }
    }
  }
`;

const HOUSEHOLD_ADD = gql`
  mutation PublicSupporterHouseholdAdd($params: PublicSupporterHouseholdAddInput) {
    publicSupporterHouseholdAdd(params: $params) {
      code
      errors { name errors }
      data { id }
    }
  }
`;

const HOUSEHOLD_REMOVE = gql`
  mutation PublicSupporterHouseholdRemove($params: PublicSupporterHouseholdRemoveInput) {
    publicSupporterHouseholdRemove(params: $params) {
      code
      errors { name errors }
      data { id }
    }
  }
`;

export class PublicSupporterService {
  /** Email a sign-in code. ⚠ The answer never says whether the address is known. */
  static async requestCode(email: string): Promise<OpResult> {
    return ApiService.executeMutation(REQUEST_CODE, { params: { email } });
  }

  /** Trade the code for this browser's session (`data[0].token`). */
  static async redeemCode(email: string, code: string): Promise<OpResult> {
    return ApiService.executeMutation(REDEEM_CODE, { params: { email, code } });
  }

  static async page(token: string): Promise<OpResult> {
    return ApiService.executeQuery(PAGE_QUERY, { params: { token } }, { fetchPolicy: "no-cache" });
  }

  static async receiptPdf(token: string, receiptId: string): Promise<OpResult> {
    return ApiService.executeQuery(RECEIPT_QUERY, { params: { token, receiptId } }, { fetchPolicy: "no-cache" });
  }

  static async householdAdd(input: { token: string; membershipId: string; firstName: string; lastName?: string | null; email?: string | null }): Promise<OpResult> {
    return ApiService.executeMutation(HOUSEHOLD_ADD, { params: input });
  }

  static async householdRemove(input: { token: string; membershipId: string; personId: string }): Promise<OpResult> {
    return ApiService.executeMutation(HOUSEHOLD_REMOVE, { params: input });
  }
}
